import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { BrowserQRCodeReader } from '@zxing/browser'
import { DecodeHintType } from '@zxing/library'
import { Html5Qrcode } from 'html5-qrcode'
import { apiClient, checkQrReceiptDuplicate } from '../services/api'
import { Camera, RefreshCcw, CheckCircle } from 'lucide-react'
import { useTranslation } from '../contexts/TranslationContext'
import ConfirmDialog from '../components/ui/ConfirmDialog'

export default function QRCodeScanner() {
  const navigate = useNavigate()
  const videoRef = useRef(null)
  const fileInputRef = useRef(null)
  const codeReaderRef = useRef(null)
  const [availableDevices, setAvailableDevices] = useState([])
  const [selectedDeviceId, setSelectedDeviceId] = useState('')
  const [scanResult, setScanResult] = useState('')
  const [qrResponse, setQrResponse] = useState(null)
  const [qrLoading, setQrLoading] = useState(false)
  const [error, setError] = useState('')
  const [successMessage, setSuccessMessage] = useState('')
  const [scanning, setScanning] = useState(false)
  const [fileScanning, setFileScanning] = useState(false)
  const [scanAttempted, setScanAttempted] = useState(false)
  const [croppedPreviewUrl, setCroppedPreviewUrl] = useState('')
  const [attemptedTransforms, setAttemptedTransforms] = useState([])
  const [categories, setCategories] = useState([])
  const [selectedCategoryId, setSelectedCategoryId] = useState('')
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [pendingQrText, setPendingQrText] = useState('')
  const [duplicateReceipt, setDuplicateReceipt] = useState(null)
  const [duplicateSubmitting, setDuplicateSubmitting] = useState(false)
  const { t } = useTranslation()

  const loadCategories = async () => {
    try {
      const response = await apiClient.get('/categories', { params: { type: 'product' } })
      setCategories((response.data?.data ?? response.data) || [])
    } catch (err) {
      console.error('Failed to load receipt categories', err)
    }
  }

  useEffect(() => {
    const hints = new Map()
    hints.set(DecodeHintType.TRY_HARDER, true)
    codeReaderRef.current = new BrowserQRCodeReader(hints)

    const loadDevices = async () => {
      try {
        const devices = await BrowserQRCodeReader.listVideoInputDevices()
        setAvailableDevices(devices)
        if (devices.length > 0) {
          setSelectedDeviceId(devices[0].deviceId)
        }
      } catch (err) {
        console.error('Failed to load video devices', err)
        setError(t('Unable to access camera devices. Please allow camera permission or try a different browser.', 'Unable to access camera devices. Please allow camera permission or try a different browser.'))
      }
    }

    loadDevices()
    loadCategories()

    return () => {
      stopScanner()
    }
  }, [])

  const startScanner = async () => {
    if (!videoRef.current) {
      setError(t('Camera element not available', 'Camera element not available'))
      return
    }

    if (!selectedDeviceId) {
      setError(t('No camera device selected', 'No camera device selected'))
      return
    }

    setError('')
    setScanResult('')
    setQrResponse(null)
    setSelectedCategoryId('')
    setScanAttempted(false)

    try {
      const codeReader = codeReaderRef.current
      if (!codeReader) return

      await codeReader.decodeFromVideoDevice(selectedDeviceId, videoRef.current, (result, err) => {
        if (result) {
          setScanResult(result.getText())
          setScanAttempted(true)
          stopScanner()
        }

        if (err && !(err.name === 'NotFoundException')) {
          console.warn('QR scan error', err)
        }
      })
      setScanning(true)
    } catch (err) {
      console.error('Failed to start QR scanner', err)
      setError(t('Unable to start the camera. Please refresh and try again.', 'Unable to start the camera. Please refresh and try again.'))
    }
  }

  const stopScanner = async () => {
    try {
      const codeReader = codeReaderRef.current
      if (codeReader) {
        await codeReader.reset()
      }
    } catch (err) {
      console.warn('Failed to stop QR scanner', err)
    } finally {
      setScanning(false)
    }
  }

  const handleDeviceChange = (event) => {
    setSelectedDeviceId(event.target.value)
  }

  const submitQr = async (text, { allowDuplicate = false, duplicateOfReceiptId = null } = {}) => {
    setQrResponse(null)
    setError('')
    setSuccessMessage('')
    setQrLoading(true)

    try {
      const payload = { url: text }
      if (selectedCategoryId) {
        payload.category_id = selectedCategoryId
      }
      if (allowDuplicate) {
        payload.allowDuplicate = true
      }
      if (duplicateOfReceiptId) {
        payload.duplicateOfReceiptId = duplicateOfReceiptId
      }

      const response = await apiClient.post('/receipts/qr', payload)
      setQrResponse({
        receipt: response.data.receipt,
        reused: false,
        receipt_url: response.data.receipt?.qr_code_link || null,
      })

      if (response.data?.success) {
        setSuccessMessage(response.data.message || t('Receipt created and queued successfully.', 'Receipt created and queued successfully.'))
        setScanResult('')
        setScanAttempted(false)
        setCroppedPreviewUrl('')
        setAttemptedTransforms([])
      }
    } catch (err) {
      console.error('Failed to analyze QR result', err)
      const backendMessage = err?.response?.data?.message
      setError(backendMessage || t('Failed to analyze the QR scan results. Please try again.', 'Failed to analyze the QR scan results. Please try again.'))
    } finally {
      setQrLoading(false)
    }
  }

  const analyzeScanResult = async (text) => {
    const trimmed = String(text || '').trim()
    if (!trimmed) return
    try {
      const preflight = await checkQrReceiptDuplicate(trimmed)
      if (preflight?.duplicate && preflight?.requiresConfirmation) {
        setPendingQrText(trimmed)
        setDuplicateReceipt(preflight.duplicateReceipt || null)
        setConfirmOpen(true)
        return
      }
      await submitQr(trimmed, { allowDuplicate: false })
    } catch (err) {
      console.error('Failed to run QR duplicate preflight', err)
      const backendMessage = err?.response?.data?.message
      setError(backendMessage || t('Failed to run duplicate check.', 'Failed to run duplicate check.'))
    }
  }

  const scanFileWithHtml5Qrcode = async (file) => {
    const tempId = `html5-qrcode-temp-${Date.now()}`
    const tempDiv = document.createElement('div')
    tempDiv.id = tempId
    tempDiv.style.display = 'none'
    document.body.appendChild(tempDiv)

    const html5Qrcode = new Html5Qrcode(tempId)
    try {
      const result = await html5Qrcode.scanFileV2(file, true)
      if (!result) {
        throw new Error('html5-qrcode did not return a result')
      }
      return result.decodedText || result?.text || result?.decodedText || ''
    } finally {
      try {
        html5Qrcode.clear()
      } catch {
        // ignore cleanup errors
      }
      if (document.body.contains(tempDiv)) {
        document.body.removeChild(tempDiv)
      }
    }
  }

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0]
    if (!file) return
    if (!file.type.startsWith('image/')) {
      setError(t('Please upload a valid image file containing a QR code.', 'Please upload a valid image file containing a QR code.'))
      return
    }

    stopScanner()
    setError('')
    setScanResult('')
    setQrResponse(null)
    setSelectedCategoryId('')
    setScanAttempted(false)
    setCroppedPreviewUrl('')
    setAttemptedTransforms([])
    setFileScanning(true)

    try {
      const reader = new FileReader()
      reader.onload = async (e) => {
        const dataUrl = e.target?.result
        if (!dataUrl || typeof dataUrl !== 'string') {
          setError(t('Failed to read the uploaded file.', 'Failed to read the uploaded file.'))
          setFileScanning(false)
          setScanAttempted(true)
          return
        }

        try {
          const image = new Image()
          image.onload = async () => {
            setAttemptedTransforms([{ label: 'full image', dataUrl }])
            try {
              const text = await scanFileWithHtml5Qrcode(file)
              setScanResult(text)
              setAttemptedTransforms([{ label: 'html5-qrcode local scan', dataUrl }])
              setCroppedPreviewUrl(dataUrl)
              setScanAttempted(true)
            } catch (scanError) {
              console.error('html5-qrcode local scan failed', scanError)
              setError(t('No QR code detected in the uploaded image, please scan whole receipt or try again', 'No QR code detected in the uploaded image, please scan whole receipt or try again'))
              setScanAttempted(true)
            } finally {
              setFileScanning(false)
            }
          }
          image.src = dataUrl
        } catch (err) {
          console.error('Failed to decode uploaded QR image', err)
          setError(t('No QR code detected in the uploaded image, please scan whole receipt or try again', 'No QR code detected in the uploaded image, please scan whole receipt or try again'))
          setFileScanning(false)
        }
      }
      reader.readAsDataURL(file)
    } catch (err) {
      console.error('QR file scan failed', err)
      setError(t('Unable to process the uploaded file.', 'Unable to process the uploaded file.'))
      setFileScanning(false)
      setScanAttempted(true)
    }
  }

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      <ConfirmDialog
        open={confirmOpen}
        title={t('This QR receipt already exists', 'This QR receipt already exists')}
        message={t('A receipt with the same QR code was already added. Do you want to add it again?', 'A receipt with the same QR code was already added. Do you want to add it again?')}
        confirmText={t('Add anyway', 'Add anyway')}
        cancelText={t('Cancel', 'Cancel')}
        loading={duplicateSubmitting}
        onCancel={() => {
          if (duplicateSubmitting) return
          setConfirmOpen(false)
          setPendingQrText('')
          setDuplicateReceipt(null)
        }}
        onConfirm={async () => {
          if (!pendingQrText) return
          setDuplicateSubmitting(true)
          try {
            await submitQr(pendingQrText, {
              allowDuplicate: true,
              duplicateOfReceiptId: duplicateReceipt?.id || null,
            })
            setConfirmOpen(false)
            setPendingQrText('')
            setDuplicateReceipt(null)
          } finally {
            setDuplicateSubmitting(false)
          }
        }}
      >
        {duplicateReceipt && (
          <div className="mt-3 text-sm text-muted-foreground">
            <p>{t('Date added', 'Date added')}: {duplicateReceipt.createdAt || '-'}</p>
            <p>{t('Status', 'Status')}: {duplicateReceipt.status || '-'}</p>
            <p>{t('Host', 'Host')}: {duplicateReceipt.host || '-'}</p>
            <p>{t('Total', 'Total')}: {(duplicateReceipt.totalOriginalAmountDecimal || '-') + ' ' + (duplicateReceipt.totalOriginalCurrency || '')}</p>
            <button
              type="button"
              className="mt-2 text-blue-600 underline"
              onClick={() => {
                const id = duplicateReceipt?.id
                setConfirmOpen(false)
                setPendingQrText('')
                setDuplicateReceipt(null)
                if (id) navigate(`/receipts/${id}`)
              }}
            >
              {t('View existing', 'View existing')}
            </button>
          </div>
        )}
      </ConfirmDialog>
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-3">
          <Camera size={28} className="text-blue-600" />
          <div>
            <h1 className="text-3xl font-bold">{t('QR Code Scanner', 'QR Code Scanner')}</h1>
            <p className="text-sm text-muted-foreground">{t('Scan a QR code using your camera and copy the captured result.', 'Scan a QR code using your camera and copy the captured result.')}</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[320px_1fr]">
        <div className="rounded-2xl border border-outline bg-surface p-6 shadow-sm">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-foreground">{t('Camera', 'Camera')}</label>
              <select
                value={selectedDeviceId}
                onChange={handleDeviceChange}
                className="mt-2 w-full rounded-lg border border-outline bg-surface px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
              >
                {availableDevices.length === 0 ? (
                  <option key="no-camera" value="">{t('No camera devices found', 'No camera devices found')}</option>
                ) : (
                  availableDevices.map((device, index) => (
                    <option key={`${device.deviceId ?? 'camera'}-${index}`} value={device.deviceId}>
                      {device.label || `Camera ${device.deviceId ?? index}`}
                    </option>
                  ))
                )}
              </select>
            </div>

            <div className="space-y-2">
              <button
                type="button"
                onClick={scanning ? stopScanner : startScanner}
                className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-3 text-white hover:bg-blue-700"
              >
                {scanning ? <RefreshCcw size={18} /> : <Camera size={18} />} 
                {scanning ? t('Stop scanning', 'Stop scanning') : t('Start scanning', 'Start scanning')}
              </button>
              <div>
                <label className="block text-sm font-medium text-foreground">{t('Upload QR Code Image', 'Upload QR Code Image')}</label>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileUpload}
                  className="mt-2 w-full rounded-lg border border-outline bg-surface px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                />
                {fileScanning && (
                  <p className="mt-2 text-sm text-muted-foreground">{t('Analyzing uploaded image...', 'Analyzing uploaded image...')}</p>
                )}
              </div>
            </div>

            {successMessage && (
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-700">
                {successMessage}
              </div>
            )}

            {error && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                {error}
              </div>
            )}

            {scanResult && (
              <div className="rounded-2xl border border-green-200 bg-green-50 p-4 space-y-4">
                <div className="flex items-start gap-2">
                  <CheckCircle size={20} className="text-green-600" />
                  <div>
                    <p className="text-sm font-semibold text-green-900">{t('QR Code scanned', 'QR Code scanned')}</p>
                    <p className="mt-1 text-sm text-foreground break-all">{scanResult}</p>
                  </div>
                </div>

                {qrLoading && (
                  <div className="rounded-lg border border-outline bg-surface p-3 text-sm text-foreground">
                    {t('Looking for a receipt link...', 'Looking for a receipt link...')}
                  </div>
                )}

                {scanResult && !qrResponse && !qrLoading && (
                  <div className="mt-3 space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-foreground">{t('Receipt category', 'Receipt category')}</label>
                      <select
                        value={selectedCategoryId}
                        onChange={(event) => setSelectedCategoryId(event.target.value)}
                        className="mt-2 w-full rounded-lg border border-outline bg-surface px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                      >
                        <option value="">{t('Optional: select a receipt category', 'Optional: select a receipt category')}</option>
                        {categories.map((category) => (
                          <option key={category.id} value={category.id}>
                            {category.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <button
                      type="button"
                      onClick={() => analyzeScanResult(scanResult)}
                      className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-3 text-sm font-semibold text-white hover:bg-blue-700"
                    >
                      {t('Process QR code result', 'Process QR code result')}
                    </button>
                  </div>
                )}
              </div>
            )}

            {qrResponse && (
              <div className="rounded-2xl border border-green-200 bg-green-50 p-4 space-y-4">
                <div className="space-y-3">
                  <div className="rounded-lg border border-outline bg-surface p-3 text-sm text-foreground">
                    <p className="font-semibold text-foreground">{t('Analysis result', 'Analysis result')}</p>
                    <p className="mt-1">
                      {qrResponse.receipt_url
                        ? qrResponse.reused
                          ? t('A matching QR receipt already exists in the system and will be reused.', 'A matching QR receipt already exists in the system and will be reused.')
                          : t('A receipt link was found in the scanned QR code.', 'A receipt link was found in the scanned QR code.')
                        : t('No receipt link was found. You can upload the receipt image instead.', 'No receipt link was found. You can upload the receipt image instead.')}
                    </p>
                  </div>

                  {qrResponse.receipt_url ? (
                    <div className="rounded-lg border border-outline bg-surface p-3 text-sm text-foreground">
                      <p className="font-semibold text-foreground">{t('Receipt link detected', 'Receipt link detected')}</p>
                      <p className="mt-1 text-sm text-muted-foreground break-all">{qrResponse.receipt_url}</p>
                    </div>
                  ) : (
                    <div className="rounded-lg border border-orange-200 bg-orange-50 p-4 text-sm text-orange-800">
                      <p>{qrResponse.fallback?.prompt || t('Upload a full receipt image to continue.', 'Upload a full receipt image to continue.')}</p>
                        <button
                          type="button"
                          onClick={() => { window.location.href = '/upload' }}
                          className="mt-3 inline-flex items-center justify-center rounded-lg bg-orange-600 px-4 py-2 text-sm font-semibold text-white hover:bg-orange-700"
                        >
                          {t('Upload receipt image', 'Upload receipt image')}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}

            {(!scanResult && scanAttempted && !qrLoading && !fileScanning) && (
              <div className="mt-4 flex flex-col gap-3 sm:flex-row">
                <button
                  type="button"
                  onClick={() => {
                    setError('')
                    setQrResponse(null)
                    setScanResult('')
                    setAttemptedTransforms([])
                    startScanner()
                  }}
                  className="inline-flex items-center justify-center rounded-lg bg-slate-700 px-4 py-3 text-sm font-semibold text-white hover:bg-slate-800"
                >
                  {t('Scan again', 'Scan again')}
                </button>
                <a
                  href="/upload"
                  className="inline-flex items-center justify-center rounded-lg bg-orange-600 px-4 py-3 text-sm font-semibold text-white hover:bg-orange-700"
                >
                  {t('Upload whole receipt', 'Upload whole receipt')}
                </a>
              </div>
            )}

          </div>
        </div>

        <div className="rounded-2xl border border-outline bg-surface p-6 shadow-sm">
          <p className="text-sm text-muted-foreground">{t('Point your camera at a QR code. The scanner will automatically capture the first valid code it detects.', 'Point your camera at a QR code. The scanner will automatically capture the first valid code it detects.')}</p>
          <div className="mt-6 rounded-2xl border border-outline bg-surface-elevated p-6 text-center text-sm text-muted-foreground">
            {croppedPreviewUrl ? (
              <img src={croppedPreviewUrl} alt="Detected QR code region" className="mx-auto max-h-[420px] w-full object-contain rounded-lg border border-outline bg-surface" />
            ) : (
              <div className="flex h-[420px] items-center justify-center">
                {scanning ? t('Scanning for QR code...', 'Scanning for QR code...') : t('QR code region will appear here once detected.', 'QR code region will appear here once detected.')}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}


